import { useCallback } from 'react';
import { Button, FormGroup } from 'reactstrap';
import { isEmpty } from 'lodash-es';
import { getCounty } from '@s20/common/util/location';
import { SmallOutlineButton, FlexStartRow, FormLabel } from '../common/forms/FormElements';
import { CountyLabel } from '../uscounties/CountyLabel';
import { Address } from '../address/Address';

const LocationSelector = ({
    label,
    type,
    stateData,
    contract,
    usCounties,
    addressFormRef,
    setSelectedAddressType,
    locationRequired,
    isDisabled,
    allowSameAs = true,
}) => {
    const location = stateData?.[type];

    // If no location, we open the address form in "create" mode.
    const onAddLocation = useCallback(() => {
        if (!contract) return;
        setSelectedAddressType(type);

        const newLocation = new Address(contract.countryId, contract.countryCode);

        const title = `New ${type === 'meetingLocation' ? 'Meeting' : 'Job'} Location`;
        addressFormRef.current.open(
            newLocation,
            title,
            allowSameAs,
            `Also use as ${type === 'meetingLocation' ? 'Job Location' : 'Meeting Location'}`,
        );
    }, [contract, type, addressFormRef, setSelectedAddressType, allowSameAs]);

    // If there's an existing location, we open it in "edit" mode.
    const onEditLocation = useCallback(() => {
        if (!location) return;
        setSelectedAddressType(type);

        const title = `Edit ${type === 'meetingLocation' ? 'Meeting' : 'Job'} Location`;
        addressFormRef.current.open(
            location,
            title,
            allowSameAs,
            `Also use as ${type === 'meetingLocation' ? 'Job Location' : 'Meeting Location'}`,
        );
    }, [location, addressFormRef, setSelectedAddressType, type, allowSameAs]);

    const county = getCounty(usCounties, {
        usCountyId: location?.usCountyId,
        contractUnionWorkTypeId: contract?.contractUnionWorkTypeId,
    });

    return (
        <FormGroup>
            <FormLabel
                htmlFor={type}
                text={label}
                required
            />
            {location && location.address1 ? (
                <FlexStartRow>
                    <SmallOutlineButton
                        onClick={onEditLocation}
                        label="Edit"
                        disabled={isDisabled}
                    />
                    <span className="form-control-plaintext ml-2">
                        {location.address1}
                        ,
                        {location.city}
                        ,
                        {location.state}
                        {' '}
                        {location.zip}
                        {' '}
                        (
                        <CountyLabel {...county} />
                        )
                    </span>
                </FlexStartRow>
            ) : (
                <Button
                    type="button"
                    color="secondary"
                    size="sm"
                    onClick={onAddLocation}
                    disabled={isDisabled}
                >
                    Add {label}
                </Button>
            )}
            {!isEmpty(locationRequired) && (
                <small className="text-danger">
                    {locationRequired}
                </small>
            )}
        </FormGroup>
    );
};

export default LocationSelector;
